import styles from './AppPromotionPopup.module.scss';

import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StringifiableRecord, stringify } from 'query-string';

import { useToggle } from '@clover-util/use-toggle';
import { useMerchant } from 'hooks/context/merchant';
import { useBowser } from 'hooks/useBowser/useBowser';

import { AppLink } from 'components/AppLink/AppLink';
import { Modal } from 'components/Modal';
import { Icon } from 'components/Icon';
import { useFeature } from '@optimizely/react-sdk';

type AppPromotionPopupProps = {
  to: string;
  query?: StringifiableRecord;
  children?: never;
  onClose?: () => void;
};

const GA_PLAY_STORE_REFERRER_QR = {
  referrer: stringify({
    utm_source: 'CWO',
    utm_medium: 'app_toast',
  }),
};

export const AppPromotionPopup: React.FC<AppPromotionPopupProps> = ({ to, query, onClose }) => {
  const { t } = useTranslation();
  const browser = useBowser();
  const { status } = useMerchant();
  const [isAndroidToastEnabled] = useFeature('android_app_toast_enabled', { autoUpdate: true });
  const [isOpen, toggle] = useToggle(status === 'online');

  useEffect(() => {
    if (status === 'online') return;

    // delay this so it opens last
    const timeoutId = setTimeout(toggle, 240);

    return () => clearTimeout(timeoutId);
  }, [status, toggle]);

  const onRequestClose = useCallback(() => {
    onClose?.();
    toggle();
  }, [toggle, onClose]);

  const params = useMemo(() => ({ ...query, ...GA_PLAY_STORE_REFERRER_QR }), [query]);

  // only show if android or ios
  if ((!browser.is('android') || !isAndroidToastEnabled /* && !browser.is('ios') */) || !isOpen) return null;

  return (
    <Modal className={styles.modal} isOpen onRequestClose={onRequestClose}>
      <ul className={styles.list}>
        <li className={styles.item}>
          <div className={styles.appWrapper}>
            <Icon className={styles.appIcon} name="cloverapp" />
            <span>{t('openInApp')}</span>
          </div>
          <AppLink className={styles.openLink} to={to} query={params}>
            {t('open')}
          </AppLink>
        </li>
        <li className={styles.item}>
          <button className={styles.continueButton} onClick={onRequestClose}>
            {t('continueOnWeb')}
          </button>
        </li>
      </ul>
    </Modal>
  );
};
